import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// ---------------------------------------------------------------------------
// useAsync
// ---------------------------------------------------------------------------

export interface AsyncState<T> {
  data: T | null;
  error: Error | null;
  loading: boolean;
}

export interface AsyncResult<T> extends AsyncState<T> {
  /** Re-runs the task. Safe to pass straight to `<ErrorState onRetry>`. */
  reload: () => void;
  /** Optimistic local update without a round trip. */
  setData: (updater: T | ((current: T | null) => T | null)) => void;
}

/**
 * Runs an async task on mount and whenever `deps` change. The task receives an
 * AbortSignal that fires when the deps change or the component unmounts, so a
 * slow response can never overwrite a newer one.
 */
export function useAsync<T>(
  task: (signal: AbortSignal) => Promise<T>,
  deps: readonly unknown[] = [],
  options: { enabled?: boolean } = {},
): AsyncResult<T> {
  const { enabled = true } = options;
  const [state, setState] = useState<AsyncState<T>>({ data: null, error: null, loading: enabled });
  const [nonce, setNonce] = useState(0);
  const taskRef = useRef(task);
  taskRef.current = task;

  useEffect(() => {
    if (!enabled) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }
    const controller = new AbortController();
    let active = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    taskRef
      .current(controller.signal)
      .then((data) => {
        if (active) setState({ data, error: null, loading: false });
      })
      .catch((err: unknown) => {
        if (!active || controller.signal.aborted) return;
        setState({ data: null, error: err instanceof Error ? err : new Error(String(err)), loading: false });
      });
    return () => {
      active = false;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, nonce, ...deps]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const setData = useCallback((updater: T | ((current: T | null) => T | null)) => {
    setState((s) => ({
      ...s,
      data: typeof updater === 'function' ? (updater as (c: T | null) => T | null)(s.data) : updater,
    }));
  }, []);

  return { ...state, reload, setData };
}

// ---------------------------------------------------------------------------
// useDebounce
// ---------------------------------------------------------------------------

/** The value, settled: it only updates once `delay` ms have passed without a change. */
export function useDebounce<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

/** A debounced wrapper around a callback, with a `cancel` escape hatch. */
export function useDebouncedCallback<A extends unknown[]>(
  callback: (...args: A) => void,
  delay = 300,
): ((...args: A) => void) & { cancel: () => void } {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;
  const timer = useRef<number | undefined>(undefined);

  const cancel = useCallback(() => {
    if (timer.current !== undefined) window.clearTimeout(timer.current);
    timer.current = undefined;
  }, []);

  useEffect(() => cancel, [cancel]);

  const run = useCallback(
    (...args: A) => {
      cancel();
      timer.current = window.setTimeout(() => callbackRef.current(...args), delay);
    },
    [cancel, delay],
  );

  return useMemo(() => Object.assign(run, { cancel }), [run, cancel]);
}

// ---------------------------------------------------------------------------
// useMediaQuery
// ---------------------------------------------------------------------------

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window === 'undefined' ? false : window.matchMedia(query).matches,
  );

  useEffect(() => {
    const list = window.matchMedia(query);
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
    setMatches(list.matches);
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/** Matches the Tailwind `lg` breakpoint, where the bottom nav becomes a sidebar. */
export function useIsDesktop(): boolean {
  return useMediaQuery('(min-width: 1024px)');
}

export function usePrefersReducedMotion(): boolean {
  return useMediaQuery('(prefers-reduced-motion: reduce)');
}

// ---------------------------------------------------------------------------
// useLocalStorage
// ---------------------------------------------------------------------------

/**
 * State mirrored into localStorage, kept in sync across tabs. Reads are lazy and
 * a corrupt entry falls back to `initialValue` instead of throwing on boot.
 */
export function useLocalStorage<T>(
  key: string,
  initialValue: T,
): [T, (value: T | ((current: T) => T)) => void, () => void] {
  const read = useCallback((): T => {
    try {
      const raw = window.localStorage.getItem(key);
      return raw === null ? initialValue : (JSON.parse(raw) as T);
    } catch {
      return initialValue;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const [stored, setStored] = useState<T>(read);

  const setValue = useCallback(
    (value: T | ((current: T) => T)) => {
      setStored((current) => {
        const next = typeof value === 'function' ? (value as (c: T) => T)(current) : value;
        try {
          window.localStorage.setItem(key, JSON.stringify(next));
        } catch {
          /* Quota or private mode: keep the in-memory value, drop the persistence. */
        }
        return next;
      });
    },
    [key],
  );

  const remove = useCallback(() => {
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* ignored */
    }
    setStored(initialValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== key) return;
      setStored(read());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [key, read]);

  return [stored, setValue, remove];
}

// ---------------------------------------------------------------------------
// Small utilities the primitives share
// ---------------------------------------------------------------------------

/** Stable auto-generated id, for label/aria wiring when no id is supplied. */
export function useAutoId(prefix = 'mk'): string {
  const ref = useRef<string | undefined>(undefined);
  if (ref.current === undefined) {
    ref.current = `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
  }
  return ref.current;
}

/** Fires when a pointer goes down outside any of the given elements. */
export function useOnClickOutside(
  refs: Array<React.RefObject<HTMLElement | null>>,
  handler: () => void,
  enabled = true,
): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!enabled) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      for (const ref of refs) {
        if (ref.current && ref.current.contains(target)) return;
      }
      handlerRef.current();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...refs]);
}

/** Locks body scroll while an overlay is open, restoring the previous value. */
export function useBodyScrollLock(locked: boolean): void {
  useEffect(() => {
    if (!locked) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [locked]);
}
